import { Link } from 'react-router-dom';

interface ArticleCardProps {
  id: number;
  title: string;
  slug: string;
  category: string;
  categorySlug: string;
  excerpt: string;
  image: string;
  author: string;
  publishDate: string;
  readCount: number;
  isHot: boolean;
  tags: string[];
}

export default function ArticleCard({
  title,
  slug,
  category,
  categorySlug,
  excerpt,
  image,
  author,
  publishDate,
  readCount,
  isHot,
}: ArticleCardProps) {
  const formatReadCount = (count: number) => {
    if (count >= 10000) {
      return `${(count / 10000).toFixed(1)}万`;
    }
    return count.toString();
  };

  return (
    <Link
      to={`/article/${slug}`}
      className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={image}
          alt={title}
          title={title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {isHot && (
          <span className="absolute top-3 left-3 px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-md whitespace-nowrap">
            热门
          </span>
        )}
        <span className="absolute top-3 right-3 px-2.5 py-1 bg-foreground-950/70 text-background-50 text-xs rounded-md whitespace-nowrap">
          {category}
        </span>
      </div>
      <div className="p-4">
        <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
          {title}
        </h3>
        <p className="mt-2 text-xs text-foreground-500 leading-relaxed line-clamp-2">
          {excerpt}
        </p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/70">
          <div className="flex items-center gap-3 text-xs text-foreground-400">
            <span>{author}</span>
            <span>{publishDate}</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-foreground-400">
            <i className="ri-eye-line"></i>
            <span>{formatReadCount(readCount)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export function ArticleCardHorizontal({
  title,
  slug,
  category,
  excerpt,
  image,
  author,
  publishDate,
  readCount,
  isHot,
}: ArticleCardProps) {
  const formatReadCount = (count: number) => {
    if (count >= 10000) {
      return `${(count / 10000).toFixed(1)}万`;
    }
    return count.toString();
  };

  return (
    <Link
      to={`/article/${slug}`}
      className="group flex flex-col sm:flex-row gap-4 bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer p-4"
    >
      <div className="relative w-full sm:w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden">
        <img
          src={image}
          alt={title}
          title={title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {isHot && (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-primary-500 text-background-50 text-xs rounded-md whitespace-nowrap">
            热门
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-primary-500 font-medium">{category}</span>
          </div>
          <h3 className="text-sm font-heading font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
            {title}
          </h3>
          <p className="mt-1 text-xs text-foreground-500 leading-relaxed line-clamp-2">
            {excerpt}
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs text-foreground-400 mt-2">
          <span>{author}</span>
          <span>{publishDate}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatReadCount(readCount)}
          </span>
        </div>
      </div>
    </Link>
  );
}